import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdPersonAdd, MdSearch, MdVisibility } from 'react-icons/md';
import { clientesAPI, usuariosAPI, gruposAPI } from '../services/api';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

const ClientsContainer = styled.div`
  max-width: 1400px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    /* remove top margin on mobile; header already handles spacing */
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    margin-bottom: ${p => p.theme.spacing.md};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const Button = styled.button`
  padding: 10px ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${props => props.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  box-shadow: 0 4px 14px ${props => props.theme.colors.primary}40;

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${props => props.theme.colors.primary}50;
  }
`;

const FilterSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.card};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.md};
    margin-bottom: ${p => p.theme.spacing.md};
    border-radius: ${p => p.theme.borderRadius.md};
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  padding-left: 2.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: ${props => props.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textLight};
  font-size: 1.25rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    gap: 6px;
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
    /* scrollable pill row */
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const FilterButton = styled.button`
  padding: 7px 18px;
  background: ${props => props.active
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
    : props.theme.colors.backgroundDark};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border: none;
  border-radius: 99px;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.active ? 600 : 500};
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.fast};
  box-shadow: ${props => props.active ? `0 4px 12px ${props.theme.colors.primary}35` : 'none'};

  &:hover {
    background: ${props => props.active
      ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
      : props.theme.colors.border};
    color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  }
`;

const ClientsListSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.card};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.md};
    border-radius: ${p => p.theme.borderRadius.md};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

/* ── Mobile card list ─────────────────────────────────── */
const MobileCardList = styled.div`
  display: none;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const ClientCard = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.985);
    box-shadow: ${p => p.theme.shadows.sm};
  }
`;

const CardAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${p => p.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardBalance = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 700;
  color: ${p => p.hasBalance ? p.theme.colors.danger : p.theme.colors.success};
  white-space: nowrap;
  flex-shrink: 0;
`;

const CardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const Thead = styled.thead``;

const Th = styled.th`
  padding: 8px ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: 700;
  color: ${props => props.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'al corriente') return props.theme.colors.success + '20';
    if (status === 'vencido') return props.theme.colors.danger + '20';
    if (status === 'pendiente') return props.theme.colors.warning + '20';
    if (status === 'sin recibos') return props.theme.colors.textSecondary + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'al corriente') return props.theme.colors.success;
    if (status === 'vencido') return props.theme.colors.danger;
    if (status === 'pendiente') return props.theme.colors.warning;
    if (status === 'sin recibos') return props.theme.colors.textSecondary;
    return props.theme.colors.warning;
  }};
`;

const ActionButton = styled.button`
  padding: 5px 14px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${props => props.theme.fonts.primary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px ${props => props.theme.colors.primary}40;
  }
`;

const ClientNameLink = styled.button`
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const ClientAvatarCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ClientAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${props => props.bg || `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  letter-spacing: 0.02em;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 6px 14px;
  background: ${props => props.active
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
    : props.theme.colors.backgroundDark};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border: none;
  border-radius: 8px;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.active ? 600 : 400};
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${props => props.active
      ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
      : props.theme.colors.border};
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AssignadoSelect = styled.select`
  padding: 7px 14px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  margin-top: ${p => p.theme.spacing.md};
  transition: ${p => p.theme.transitions.fast};

  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const Clients = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usuarios, setUsuarios] = useState([]);
  const [asignadoFilter, setAsignadoFilter] = useState('');
  const [grupos, setGrupos] = useState([]);
  const [grupoFilter, setGrupoFilter] = useState('');
  const clientsPerPage = 10;

  useEffect(() => {
    loadClientes();
  }, []);

  useEffect(() => {
    usuariosAPI.getAll().then(r => setUsuarios(r.data.data || [])).catch(() => {});
    gruposAPI.getAll().then(r => setGrupos(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, asignadoFilter, grupoFilter]);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await clientesAPI.getAll();
      setClientes(response.data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar clientes');
      console.error('Error al cargar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const getClientStatus = (cliente) => {
    const saldo = parseFloat(cliente.saldo_total ?? cliente.saldo_pendiente ?? 0);

    // Sin recibos
    if (!cliente.total_recibos || cliente.total_recibos === 0) return 'Sin recibos';

    // Tiene recibos vencidos
    if (cliente.recibos_vencidos > 0) return 'Vencido';

    // Saldo completamente pagado
    if (saldo === 0) return 'Al corriente';

    // Tiene saldo pendiente pero sin vencer
    if (saldo > 0) return 'Pendiente';

    return 'Al corriente';
  };

  const filteredClients = clientes.filter(client => {
    // Los sub-clientes viven dentro de su cliente padre: no se listan sueltos
    // aquí, solo se ven/gestionan desde la ficha del padre.
    if (client.padre_cliente_id) return false;
    const status = getClientStatus(client);
    const matchesFilter = filter === 'Todos' || status === filter;
    const matchesSearch = client.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.rfc && client.rfc.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchAsignado = !asignadoFilter || String(client.asignado_a) === asignadoFilter;
    const matchGrupo = !grupoFilter || String(client.grupo_id) === grupoFilter;
    return matchesFilter && matchesSearch && matchAsignado && matchGrupo;
  });

  // Calcular paginación sobre clientes filtrados
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getAvatarGradient = (nombre) => {
    const gradients = [
      'linear-gradient(135deg,#4FD1C5,#38B2AC)',
      'linear-gradient(135deg,#F6AD55,#ED8936)',
      'linear-gradient(135deg,#63B3ED,#4299E1)',
      'linear-gradient(135deg,#68D391,#48BB78)',
      'linear-gradient(135deg,#FC8181,#F56565)',
      'linear-gradient(135deg,#B794F4,#9F7AEA)',
    ];
    const idx = (nombre?.charCodeAt(0) || 0) % gradients.length;
    return gradients[idx];
  };

  const getInitials = (nombre) =>
    (nombre || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <ClientsContainer>
      <PageHeader>
        <Title>Gestión de Clientes</Title>
        <Button onClick={() => navigate('/clientes/nuevo')}>
          <MdPersonAdd />
          Nuevo Cliente
        </Button>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Buscar cliente por nombre, RFC o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterButtons>
          <FilterButton active={filter === 'Todos'} onClick={() => setFilter('Todos')}>
            Todos ({clientes.length})
          </FilterButton>
          <FilterButton active={filter === 'Al corriente'} onClick={() => setFilter('Al corriente')}>
            Al corriente ({clientes.filter(c => getClientStatus(c) === 'Al corriente').length})
          </FilterButton>
          <FilterButton active={filter === 'Pendiente'} onClick={() => setFilter('Pendiente')}>
            Pendiente ({clientes.filter(c => getClientStatus(c) === 'Pendiente').length})
          </FilterButton>
          <FilterButton active={filter === 'Vencido'} onClick={() => setFilter('Vencido')}>
            Vencido ({clientes.filter(c => getClientStatus(c) === 'Vencido').length})
          </FilterButton>
        </FilterButtons>
        {usuarios.length > 0 && (
          <AssignadoSelect value={asignadoFilter} onChange={e => setAsignadoFilter(e.target.value)}>
            <option value="">Asignado a: Todos</option>
            {usuarios.map(u => (
              <option key={u.id} value={String(u.id)}>{u.nombre}</option>
            ))}
          </AssignadoSelect>
        )}
        {grupos.length > 0 && (
          <AssignadoSelect value={grupoFilter} onChange={e => setGrupoFilter(e.target.value)}>
            <option value="">Grupo: Todos</option>
            {grupos.map(g => (
              <option key={g.id} value={String(g.id)}>{g.nombre}</option>
            ))}
          </AssignadoSelect>
        )}
      </FilterSection>

      <ClientsListSection>
        <SectionTitle>Lista de Clientes ({filteredClients.length})</SectionTitle>

        {/* Desktop: tabla */}
        <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Nombre del Cliente</Th>
              <Th>RFC</Th>
              <Th>Teléfono</Th>
              <Th>Saldo Pendiente</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </Thead>
          <tbody>
            {loading ? (
              <SkeletonTableRows rows={8} cols={6} />
            ) : currentClients.length > 0 ? (
              currentClients.map(client => (
                <tr key={client.id}>
                  <Td>
                    <ClientAvatarCell>
                      <ClientAvatar bg={getAvatarGradient(client.nombre)}>
                        {getInitials(client.nombre)}
                      </ClientAvatar>
                      <div>
                        <ClientNameLink onClick={() => navigate(`/clientes/${client.id}`)}>
                          {client.nombre}
                        </ClientNameLink>
                        {client.grupo_nombre && (
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                            {client.grupo_nombre}
                          </div>
                        )}
                        {client.total_subclientes > 0 && (
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                            +{client.total_subclientes} sub-cliente{client.total_subclientes === 1 ? '' : 's'}
                          </div>
                        )}
                      </div>
                    </ClientAvatarCell>
                  </Td>
                  <Td>{client.rfc || 'N/A'}</Td>
                  <Td>{client.telefono || 'N/A'}</Td>
                  <Td>${parseFloat(client.saldo_total ?? client.saldo_pendiente ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td>
                    <StatusBadge status={getClientStatus(client)}>
                      {getClientStatus(client)}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => navigate(`/clientes/${client.id}`)}>
                      <MdVisibility />
                      Ver Detalles
                    </ActionButton>
                  </Td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">
                  <EmptyState
                    type="clients"
                    title={searchTerm || filter !== 'Todos' ? 'Sin resultados' : 'Sin clientes aún'}
                    subtitle={searchTerm || filter !== 'Todos'
                      ? 'Intenta con otro término de búsqueda o cambia el filtro.'
                      : 'Agrega tu primer cliente para empezar a gestionar pagos.'}
                    actionLabel={!searchTerm && filter === 'Todos' ? 'Agregar Cliente' : undefined}
                    onAction={!searchTerm && filter === 'Todos' ? () => navigate('/clientes/nuevo') : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        </TableWrapper>

        {/* Mobile: tarjetas */}
        <MobileCardList>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-light)' }}>Cargando...</div>
          ) : currentClients.length > 0 ? (
            currentClients.map(client => {
              const status = getClientStatus(client);
              const saldo = parseFloat(client.saldo_total ?? client.saldo_pendiente ?? 0);
              return (
                <ClientCard key={client.id} onClick={() => navigate(`/clientes/${client.id}`)}>
                  <CardAvatar bg={getAvatarGradient(client.nombre)}>
                    {getInitials(client.nombre)}
                  </CardAvatar>
                  <CardBody>
                    <CardName>{client.nombre}</CardName>
                    <CardMeta>
                      {client.grupo_nombre ? `${client.grupo_nombre} · ` : ''}
                      {client.total_subclientes > 0 ? `+${client.total_subclientes} sub-cliente${client.total_subclientes === 1 ? '' : 's'} · ` : ''}
                      {client.telefono || client.rfc || client.email || 'Sin contacto'}
                    </CardMeta>
                  </CardBody>
                  <CardRight>
                    <StatusBadge status={status}>{status}</StatusBadge>
                    <CardBalance hasBalance={saldo > 0}>
                      ${saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </CardBalance>
                  </CardRight>
                </ClientCard>
              );
            })
          ) : (
            <EmptyState
              type="clients"
              title={searchTerm || filter !== 'Todos' ? 'Sin resultados' : 'Sin clientes aún'}
              subtitle={searchTerm || filter !== 'Todos'
                ? 'Intenta con otro término de búsqueda o cambia el filtro.'
                : 'Agrega tu primer cliente para empezar a gestionar pagos.'}
              actionLabel={!searchTerm && filter === 'Todos' ? 'Agregar Cliente' : undefined}
              onAction={!searchTerm && filter === 'Todos' ? () => navigate('/clientes/nuevo') : undefined}
            />
          )}
        </MobileCardList>

        {totalPages > 1 && (
          <Pagination>
            <PageButton 
              onClick={handlePrevious} 
              disabled={currentPage === 1}
            >
              Anterior
            </PageButton>
            
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} style={{ padding: '0 8px' }}>...</span>
              ) : (
                <PageButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              )
            ))}
            
            <PageButton 
              onClick={handleNext} 
              disabled={currentPage === totalPages}
            >
              Siguiente
            </PageButton>
          </Pagination>
        )}
      </ClientsListSection>
    </ClientsContainer>
  );
};

export default Clients;
